// src/features/AdminDashboard/Pages/AccessModificationReportPage.tsx



import {

  useCallback,

  useEffect,

  useState,

} from 'react';



import {

  ArrowLeft,

  CalendarDays,

  ChevronLeft,

  ChevronRight,

  Download,

  RefreshCw,

  Search,

  ShieldCheck,

} from 'lucide-react';



import {

  useNavigate,

} from 'react-router-dom';



import {

  getAccessModificationReport,

  getAllAccessModificationRecords,

} from '../reports/services/publicReport.service';



import type {

  AccessModificationRecord,

} from '../reports/types/publicReport.types';





// ============================================================

// PAGE SIZE

// ============================================================



const PAGE_SIZE =

  10;





// ============================================================

// DATE FORMATTER

// ============================================================



const formatModifiedOn = (

  value: string,

): string => {



  if (!value) {

    return '-';

  }





  const date =

    new Date(

      value,

    );





  if (

    Number.isNaN(

      date.getTime(),

    )

  ) {



    return value;

  }





  return date.toLocaleString(

    'en-IN',

    {

      day: '2-digit',

      month: 'short',

      year: 'numeric',

      hour: '2-digit',

      minute: '2-digit',

      hour12: true,

    },

  );

};





// ============================================================

// CSV VALUE

// ============================================================



const escapeCsvValue = (

  value: unknown,

): string => {



  const text =

    String(

      value ?? '',

    );





  return `"${text.replace(

    /"/g,

    '""',

  )}"`;

};





// ============================================================

// PAGE

// ============================================================



const AccessModificationReportPage = () => {



  const navigate =

    useNavigate();





  // ==========================================================

  // FILTERS

  // ==========================================================



  const [

    fromDate,

    setFromDate,

  ] = useState<string>('');





  const [

    toDate,

    setToDate,

  ] = useState<string>('');
  const [
    appliedFromDate,
    setAppliedFromDate,
  ] = useState<string | null>(null);

  const [
    appliedToDate,
    setAppliedToDate,
  ] = useState<string | null>(null);






  // ==========================================================

  // DATA

  // ==========================================================



  const [

    records,

    setRecords,

  ] = useState<

    AccessModificationRecord[]

  >([]);





  // ==========================================================

  // SEARCH

  // ==========================================================



  const [

    search,

    setSearch,

  ] = useState<string>('');
  const [
    appliedSearch,
    setAppliedSearch,
  ] = useState<string | null>(null);






  // ==========================================================

  // PAGINATION

  // ==========================================================



  const [

    pageNumber,

    setPageNumber,

  ] = useState<number>(1);





  const [

    totalRecords,

    setTotalRecords,

  ] = useState<number>(0);





  const [

    totalPages,

    setTotalPages,

  ] = useState<number>(0);





  // ==========================================================

  // LOADING

  // ==========================================================



  const [

    loading,

    setLoading,

  ] = useState<boolean>(false);





  const [

    exporting,

    setExporting,

  ] = useState<boolean>(false);





  // ==========================================================

  // ERROR

  // ==========================================================



  const [

    error,

    setError,

  ] = useState<string | null>(null);





  // ==========================================================

  // LOAD REPORT

  // ==========================================================



  const loadReport =

    useCallback(

      async (

        requestedPage: number,

      ) => {



        try {



          setLoading(true);



          setError(null);





          const response =

            await getAccessModificationReport({



              fromDate: appliedFromDate,

              toDate: appliedToDate,



              pageNumber:

                requestedPage,



              pageSize:

                PAGE_SIZE,



              search:

                appliedSearch?.trim() || null,



            });



          setRecords(

            Array.isArray(

              response.data,

            )

              ? response.data

              : [],

          );





          setTotalRecords(

            response.totalRecords ??

            0,

          );





          setTotalPages(

            response.totalPages ??

            0,

          );



        } catch (

          requestError

        ) {



          console.error(

            'Access Modification Report API error:',

            requestError,

          );





          setRecords([]);



          setTotalRecords(0);



          setTotalPages(0);





          setError(

            'Unable to load Access Modification report.',

          );



        } finally {



          setLoading(false);

        }



      },

      [
        appliedFromDate,
        appliedToDate,
        appliedSearch,
      ],

    );





  // ==========================================================

  // INITIAL LOAD

  // ==========================================================



  useEffect(

    () => {



      void loadReport(

        pageNumber,

      );



    },

    [

      loadReport,

      pageNumber,

    ],

  );





  // ==========================================================

  // APPLY DATE FILTER

  // ==========================================================



  const handleApplyFilters =

    () => {



      if (

        fromDate &&

        toDate &&

        fromDate > toDate

      ) {



        setError(

          'From Date cannot be greater than To Date.',

        );



        return;

      }





      setAppliedFromDate(fromDate || null);

      setAppliedToDate(toDate || null);

      setAppliedSearch(search.trim() || null);

      setPageNumber(1);



    };





  // ==========================================================

  // RESET FILTERS

  // ==========================================================



  const handleResetFilters =

    () => {



      setFromDate('');



      setToDate('');



      setAppliedFromDate(null);

      setAppliedToDate(null);

      setSearch('');
      setAppliedSearch(null);



      setPageNumber(1);



      setError(null);

    };





  // ==========================================================

  // PAGE CHANGE

  // ==========================================================



  const handlePageChange =

    (

      newPage: number,

    ) => {



      if (

        newPage < 1

      ) {



        return;

      }





      if (

        totalPages > 0 &&

        newPage > totalPages

      ) {



        return;

      }





      setPageNumber(

        newPage,

      );

    };





  const handleExportCsv =

    async () => {



      try {



        setExporting(true);



        setError(null);





        const allRecords =

          await getAllAccessModificationRecords(
            appliedFromDate,
            appliedToDate,
            appliedSearch,
          );





        if (

          allRecords.length === 0

        ) {



          setError(

            'No records available for export.',

          );



          return;

        }





        const header = [

          'Employee Role History ID',

          'Employee Code',

          'Employee Name',

          'Old Role',

          'New Role',

          'Modified On',

        ];





        const rows =

          allRecords.map(

            (

              record,

            ) => [



              record.employeeRoleHistoryId,



              record.employeeCode,



              record.employeeName,



              record.oldRole,



              record.newRole,



              formatModifiedOn(

                record.modifiedOn,

              ),



            ],

          );





        const csv =

          [

            header,

            ...rows,

          ]

            .map(

              (

                row,

              ) =>

                row

                  .map(

                    escapeCsvValue,

                  )

                  .join(','),

            )

            .join('\r\n');





        const blob =

          new Blob(

            [

              csv,

            ],

            {

              type:

                'text/csv;charset=utf-8;',

            },

          );





        const url =

          window.URL.createObjectURL(

            blob,

          );





        const link =

          document.createElement(

            'a',

          );





        const datePart =

          new Date()

            .toISOString()

            .slice(

              0,

              10,

            );





        link.href =

          url;



        link.download =

          `AccessModification_${datePart}.csv`;



        link.style.display =

          'none';





        document.body.appendChild(

          link,

        );





        link.click();



        link.remove();





        window.URL.revokeObjectURL(

          url,

        );



      } catch (

        exportError

      ) {



        console.error(

          'Access Modification CSV export error:',

          exportError,

        );





        setError(

          'Unable to export Access Modification report.',

        );



      } finally {



        setExporting(false);

      }

    };





  // ==========================================================

  // PAGINATION LABEL

  // ==========================================================



  const startRecord =

    totalRecords === 0

      ? 0

      : (

          (

            pageNumber - 1

          ) *

          PAGE_SIZE

        ) + 1;





  const endRecord =

    Math.min(

      pageNumber *

        PAGE_SIZE,

      totalRecords,

    );





  // ==========================================================

  // RENDER

  // ==========================================================



  return (

    <main

      className="

        min-h-screen

        bg-[#eef3f8]

        px-4

        py-6

        text-slate-900

        sm:px-6

        lg:px-8

      "

    >



      <div

        className="

          mx-auto

          w-full

          max-w-[1440px]

        "

      >



        {/* ==================================================

            HEADER

        =================================================== */}



        <section

          className="

            mb-5

            rounded-2xl

            border

            border-slate-200

            bg-white

            p-5

            shadow-[0_2px_10px_rgba(15,23,42,0.04)]

          "

        >



          <div

            className="

              flex

              flex-col

              gap-4

              lg:flex-row

              lg:items-center

              lg:justify-between

            "

          >



            <div

              className="

                flex

                items-center

                gap-3

              "

            >



              <button

                type="button"

                onClick={() =>

                  navigate(

                    -1,

                  )

                }

                className="

                  flex

                  h-9

                  w-9

                  shrink-0

                  items-center

                  justify-center

                  rounded-lg

                  border

                  border-slate-200

                  bg-white

                  text-slate-500

                  transition

                  hover:bg-slate-50

                  hover:text-slate-800

                "

                title="Back"

              >



                <ArrowLeft

                  size={17}

                />



              </button>





              <div

                className="

                  flex

                  h-11

                  w-11

                  shrink-0

                  items-center

                  justify-center

                  rounded-xl

                  bg-teal-50

                  text-teal-600

                "

              >



                <ShieldCheck

                  size={22}

                  strokeWidth={1.8}

                />



              </div>





              <div>



                <h1

                  className="

                    text-xl

                    font-semibold

                    tracking-tight

                    text-slate-900

                  "

                >

                  Access Modification

                </h1>





                <p

                  className="

                    mt-1

                    text-sm

                    text-slate-500

                  "

                >

                  View access and role changes made for existing WorkVista users.

                </p>



              </div>



            </div>





            <button

              type="button"

              onClick={

                handleExportCsv

              }

              disabled={

                exporting ||

                totalRecords === 0

              }

              className="

                inline-flex

                items-center

                justify-center

                gap-2

                rounded-lg

                bg-teal-600

                px-4

                py-2.5

                text-sm

                font-medium

                text-white

                transition

                hover:bg-teal-700

                disabled:cursor-not-allowed

                disabled:opacity-50

              "

            >



              {exporting ? (



                <RefreshCw

                  size={16}

                  className="animate-spin"

                />



              ) : (



                <Download

                  size={16}

                />



              )}



              {exporting

                ? 'Exporting...'

                : 'Export CSV'}



            </button>



          </div>



        </section>





        {/* ==================================================

            FILTER CARD

        =================================================== */}



        <section

          className="

            mb-5

            rounded-2xl

            border

            border-slate-200

            bg-white

            p-5

            shadow-[0_2px_10px_rgba(15,23,42,0.04)]

          "

        >



          <div

            className="

              grid

              grid-cols-1

              gap-4

              md:grid-cols-2

              lg:grid-cols-4

            "

          >



            {/* ============================================

                FROM DATE

            ============================================= */}



            <div>



              <label

                className="

                  mb-1.5

                  block

                  text-xs

                  font-semibold

                  text-slate-600

                "

              >

                From Date

              </label>





              <div

                className="

                  relative

                "

              >



                <CalendarDays

                  size={16}

                  className="

                    pointer-events-none

                    absolute

                    left-3

                    top-1/2

                    -translate-y-1/2

                    text-slate-400

                  "

                />





                <input

                  type="date"

                  value={

                    fromDate

                  }

                  onChange={(event) =>

                    setFromDate(

                      event.target.value,

                    )

                  }

                  className="

                    h-10

                    w-full

                    rounded-lg

                    border

                    border-slate-200

                    bg-white

                    pl-10

                    pr-3

                    text-sm

                    text-slate-700

                    outline-none

                    transition

                    focus:border-teal-500

                    focus:ring-2

                    focus:ring-teal-100

                  "

                />



              </div>



            </div>





            {/* ============================================

                TO DATE

            ============================================= */}



            <div>



              <label

                className="

                  mb-1.5

                  block

                  text-xs

                  font-semibold

                  text-slate-600

                "

              >

                To Date

              </label>





              <div

                className="

                  relative

                "

              >



                <CalendarDays

                  size={16}

                  className="

                    pointer-events-none

                    absolute

                    left-3

                    top-1/2

                    -translate-y-1/2

                    text-slate-400

                  "

                />





                <input

                  type="date"

                  value={

                    toDate

                  }

                  onChange={(event) =>

                    setToDate(

                      event.target.value,

                    )

                  }

                  className="

                    h-10

                    w-full

                    rounded-lg

                    border

                    border-slate-200

                    bg-white

                    pl-10

                    pr-3

                    text-sm

                    text-slate-700

                    outline-none

                    transition

                    focus:border-teal-500

                    focus:ring-2

                    focus:ring-teal-100

                  "

                />



              </div>



            </div>





            {/* ============================================

                SEARCH

            ============================================= */}



            <div>



              <label

                className="

                  mb-1.5

                  block

                  text-xs

                  font-semibold

                  text-slate-600

                "

              >

                Search

              </label>





              <div

                className="

                  relative

                "

              >



                <Search

                  size={16}

                  className="

                    pointer-events-none

                    absolute

                    left-3

                    top-1/2

                    -translate-y-1/2

                    text-slate-400

                  "

                />





                <input

                  type="text"

                  value={

                    search

                  }

                  onChange={(event) =>

                    setSearch(

                      event.target.value,

                    )

                  }

                  placeholder="Employee code, name or role"

                  className="

                    h-10

                    w-full

                    rounded-lg

                    border

                    border-slate-200

                    bg-white

                    pl-10

                    pr-3

                    text-sm

                    text-slate-700

                    outline-none

                    placeholder:text-slate-400

                    focus:border-teal-500

                    focus:ring-2

                    focus:ring-teal-100

                  "

                />



              </div>



            </div>





            {/* ============================================

                ACTIONS

            ============================================= */}



            <div

              className="

                flex

                items-end

                gap-2

              "

            >



              <button

                type="button"

                onClick={

                  handleApplyFilters

                }

                className="

                  h-10

                  flex-1

                  rounded-lg

                  bg-teal-600

                  px-4

                  text-sm

                  font-medium

                  text-white

                  transition

                  hover:bg-teal-700

                "

              >

                Apply

              </button>





              <button

                type="button"

                onClick={

                  handleResetFilters

                }

                className="

                  h-10

                  rounded-lg

                  border

                  border-slate-200

                  bg-white

                  px-4

                  text-sm

                  font-medium

                  text-slate-600

                  transition

                  hover:bg-slate-50

                "

              >

                Reset

              </button>



            </div>



          </div>





          {/* =================================================

              ERROR

          ================================================== */}



          {error && (



            <div

              className="

                mt-4

                rounded-lg

                border

                border-rose-200

                bg-rose-50

                px-4

                py-3

                text-sm

                text-rose-700

              "

            >

              {error}

            </div>



          )}



        </section>





        {/* ==================================================

            TABLE CARD

        =================================================== */}



        <section

          className="

            overflow-hidden

            rounded-2xl

            border

            border-slate-200

            bg-white

            shadow-[0_2px_10px_rgba(15,23,42,0.04)]

          "

        >



          {/* =================================================

              TABLE HEADER

          ================================================== */}



          <div

            className="

              flex

              flex-col

              gap-2

              border-b

              border-slate-200

              px-5

              py-4

              sm:flex-row

              sm:items-center

              sm:justify-between

            "

          >



            <div>



              <h2

                className="

                  text-sm

                  font-semibold

                  text-slate-900

                "

              >

                Access Modification Records

              </h2>





              <p

                className="

                  mt-1

                  text-xs

                  text-slate-500

                "

              >

                {totalRecords} total record

                {totalRecords === 1

                  ? ''

                  : 's'}

              </p>



            </div>





            <div

              className="

                text-xs

                text-slate-400

              "

            >

              Page {pageNumber}

              {totalPages > 0

                ? ` of ${totalPages}`

                : ''}

            </div>



          </div>





          {/* =================================================

              TABLE

          ================================================== */}



          <div

            className="

              overflow-x-auto

            "

          >



            <table

              className="

                min-w-[900px]

                w-full

                border-collapse

              "

            >



              <thead>



                <tr

                  className="

                    border-b

                    border-slate-200

                    bg-slate-50

                  "

                >



                  <th

                    className="

                      px-5

                      py-3

                      text-left

                      text-[11px]

                      font-semibold

                      uppercase

                      tracking-wide

                      text-slate-500

                    "

                  >

                    Employee Code

                  </th>





                  <th

                    className="

                      px-5

                      py-3

                      text-left

                      text-[11px]

                      font-semibold

                      uppercase

                      tracking-wide

                      text-slate-500

                    "

                  >

                    Employee Name

                  </th>





                  <th

                    className="

                      px-5

                      py-3

                      text-left

                      text-[11px]

                      font-semibold

                      uppercase

                      tracking-wide

                      text-slate-500

                    "

                  >

                    Old Role

                  </th>





                  <th

                    className="

                      px-5

                      py-3

                      text-left

                      text-[11px]

                      font-semibold

                      uppercase

                      tracking-wide

                      text-slate-500

                    "

                  >

                    New Role

                  </th>





                  <th

                    className="

                      px-5

                      py-3

                      text-left

                      text-[11px]

                      font-semibold

                      uppercase

                      tracking-wide

                      text-slate-500

                    "

                  >

                    Modified On

                  </th>



                </tr>



              </thead>





              <tbody>



                {loading ? (



                  <tr>



                    <td

                      colSpan={5}

                      className="

                        px-5

                        py-14

                        text-center

                      "

                    >



                      <RefreshCw

                        size={22}

                        className="

                          mx-auto

                          animate-spin

                          text-teal-600

                        "

                      />



                      <p

                        className="

                          mt-3

                          text-sm

                          text-slate-500

                        "

                      >

                        Loading access modification records...

                      </p>



                    </td>



                  </tr>



                ) : records.length === 0 ? (



                  <tr>



                    <td

                      colSpan={5}

                      className="

                        px-5

                        py-14

                        text-center

                      "

                    >



                      <ShieldCheck

                        size={28}

                        className="

                          mx-auto

                          text-slate-300

                        "

                      />



                      <p

                        className="

                          mt-3

                          text-sm

                          font-medium

                          text-slate-600

                        "

                      >

                        No records found

                      </p>



                      <p

                        className="

                          mt-1

                          text-xs

                          text-slate-400

                        "

                      >

                        Try changing the selected date range.

                      </p>



                    </td>



                  </tr>



                ) : (



                  records.map(

                    (

                      record,

                    ) => (



                      <tr

                        key={

                          record.employeeRoleHistoryId

                        }

                        className="

                          border-b

                          border-slate-100

                          transition

                          last:border-b-0

                          hover:bg-slate-50/70

                        "

                      >



                        {/* Employee Code */}



                        <td

                          className="

                            whitespace-nowrap

                            px-5

                            py-4

                            text-sm

                            font-medium

                            text-slate-800

                          "

                        >

                          {record.employeeCode}

                        </td>





                        {/* Employee Name */}



                        <td

                          className="

                            px-5

                            py-4

                            text-sm

                            text-slate-700

                          "

                        >

                          {record.employeeName}

                        </td>





                        {/* Old Role */}



                        <td

                          className="

                            px-5

                            py-4

                            text-sm

                            text-slate-600

                          "

                        >



                          <span

                            className="

                              inline-flex

                              rounded-md

                              bg-slate-100

                              px-2.5

                              py-1

                              text-xs

                              font-medium

                              text-slate-600

                            "

                          >

                            {record.oldRole}

                          </span>



                        </td>





                        {/* New Role */}



                        <td

                          className="

                            px-5

                            py-4

                            text-sm

                            text-slate-600

                          "

                        >



                          <span

                            className="

                              inline-flex

                              rounded-md

                              bg-teal-50

                              px-2.5

                              py-1

                              text-xs

                              font-medium

                              text-teal-700

                            "

                          >

                            {record.newRole}

                          </span>



                        </td>





                        {/* Modified On */}



                        <td

                          className="

                            whitespace-nowrap

                            px-5

                            py-4

                            text-sm

                            text-slate-500

                          "

                        >

                          {formatModifiedOn(

                            record.modifiedOn,

                          )}

                        </td>



                      </tr>



                    ),

                  )



                )}



              </tbody>



            </table>



          </div>





          {/* =================================================

              PAGINATION

          ================================================== */}



          <div

            className="

              flex

              flex-col

              gap-3

              border-t

              border-slate-200

              px-5

              py-4

              sm:flex-row

              sm:items-center

              sm:justify-between

            "

          >



            <div

              className="

                text-xs

                text-slate-500

              "

            >



              Showing{' '}



              <span

                className="

                  font-semibold

                  text-slate-700

                "

              >

                {startRecord}

              </span>



              {' '}to{' '}



              <span

                className="

                  font-semibold

                  text-slate-700

                "

              >

                {endRecord}

              </span>



              {' '}of{' '}



              <span

                className="

                  font-semibold

                  text-slate-700

                "

              >

                {totalRecords}

              </span>



            </div>





            <div

              className="

                flex

                items-center

                gap-2

              "

            >



              <button

                type="button"

                disabled={

                  loading ||

                  pageNumber <= 1

                }

                onClick={() =>

                  handlePageChange(

                    pageNumber - 1,

                  )

                }

                className="

                  flex

                  h-9

                  w-9

                  items-center

                  justify-center

                  rounded-lg

                  border

                  border-slate-200

                  bg-white

                  text-slate-500

                  transition

                  hover:bg-slate-50

                  disabled:cursor-not-allowed

                  disabled:opacity-40

                "

              >



                <ChevronLeft

                  size={17}

                />



              </button>





              <span

                className="

                  min-w-[90px]

                  text-center

                  text-xs

                  font-medium

                  text-slate-600

                "

              >

                Page {pageNumber}

                {' / '}

                {totalPages || 1}

              </span>





              <button

                type="button"

                disabled={

                  loading ||

                  totalPages === 0 ||

                  pageNumber >= totalPages

                }

                onClick={() =>

                  handlePageChange(

                    pageNumber + 1,

                  )

                }

                className="

                  flex

                  h-9

                  w-9

                  items-center

                  justify-center

                  rounded-lg

                  border

                  border-slate-200

                  bg-white

                  text-slate-500

                  transition

                  hover:bg-slate-50

                  disabled:cursor-not-allowed

                  disabled:opacity-40

                "

              >



                <ChevronRight

                  size={17}

                />



              </button>



            </div>



          </div>



        </section>



      </div>



    </main>

  );

};





export default AccessModificationReportPage;